import React, { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { fetchJson } from '../utils/fetchJson';
import { UserContext } from './UserContext';

const PhaseContext = createContext(null);
const STORAGE_KEY = 'schoolAdmin_activePhase';
export const PHASES = { NUR_PRI: 'nur_pri', SEC: 'sec' };

export function PhaseProvider({ children }) {
  const [activePhase, setActivePhase] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [phaseCatalog, setPhaseCatalog] = useState(() => {
    try {
      const raw = localStorage.getItem('phaseCatalog');
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  });
  // School's actual assigned phases (fetched from backend)
  const [schoolPhases, setSchoolPhases] = useState(() => {
    try {
      const raw = localStorage.getItem('schoolPhases');
      return raw ? JSON.parse(raw) : null; // null = not yet loaded
    } catch (_) { return null; }
  });
  const liveUser = useContext(UserContext);
  const user = liveUser !== undefined ? liveUser : null;

  useEffect(() => {
    if (activePhase) localStorage.setItem(STORAGE_KEY, activePhase);
    // When phase code changes, try to persist its id too for API calls that prefer phaseId
    try {
      const code = (activePhase || '').toString().trim().toLowerCase();
      if (code && Array.isArray(phaseCatalog) && phaseCatalog.length) {
        const found = phaseCatalog.find(p => (p.code || '').toLowerCase() === code);
        if (found && found.id) {
          localStorage.setItem('schoolAdmin_activePhaseId', String(found.id));
        }
      }
    } catch (_) {}
  }, [activePhase, phaseCatalog]);

  const updatePhase = useCallback((code) => {
    const v = code || '';
    setActivePhase(v);
    // Immediately persist corresponding phaseId if catalog is present to avoid UI lag
    try {
      const c = (v || '').toString().trim().toLowerCase();
      if (c && Array.isArray(phaseCatalog) && phaseCatalog.length) {
        const found = phaseCatalog.find(p => (p.code || '').toLowerCase() === c);
        if (found && found.id) {
          localStorage.setItem('schoolAdmin_activePhaseId', String(found.id));
        }
      }
    } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('app:phase:changed', { detail: { code: v } }));
    } catch (_) {}
  }, [phaseCatalog]);

  const getPhaseLabel = useCallback((code) => {
    if (!code) return '—';
    if (code === 'sec') return 'Secondary';
    if (code === 'nur_pri') return 'Nursery & Primary';
    return code;
  }, []);

  const getPhaseDescription = useCallback((code) => {
    if (code === PHASES.SEC) return 'Secondary phase (General or Technical)';
    if (code === PHASES.NUR_PRI) return 'Nursery and Primary phases';
    return 'Unknown phase';
  }, []);

  const role = (user?.role === 'PrincipalHeadTeacher') ? 'Principal' : (user?.role || 'SchoolAdmin');
  const isSchoolAdmin = role === 'SchoolAdmin';
  const fixedPhase = (() => {
    if (role === 'Principal') return PHASES.SEC;
    if (role === 'HeadMaster') return PHASES.NUR_PRI;

    // A TEACHER'S PHASE IS A FACT ON THEIR OWN RECORD, and /api/users/me now
    // sends it. Nothing here infers it any more.
    //
    // It used to be inferred: a Teacher holding subjectHeadOf /
    // subjectGroupsHeaded / departmentHeadOf "must be" secondary. That was a
    // workaround for this endpoint not returning the phase, and it was wrong in
    // both directions.
    //
    // It moved a nur_pri teacher with a stray Department row INTO secondary,
    // which handed them the head-of-department pages the phase gates were
    // written to keep out. When I stopped the server reporting those lists to
    // prevent that, it removed the only evidence a GENUINE secondary subject
    // head was secondary — so she resolved to no fixed phase, fell back to
    // whatever localStorage held, and lost her pages. One inference, two
    // opposite failures, both invisible from the code that read the result.
    //
    // `phaseCode` comes straight from User.phaseId (server/routes/users.js).
    // `phaseId` is the fallback for a cached profile saved before this field
    // existed, so an old session does not lose its phase on the first load.
    if (role === 'Teacher' || role === 'ClassMaster' || role === 'SubjectHead') {
      const own = String(user?.phaseCode || '').trim().toLowerCase();
      if (own === PHASES.NUR_PRI || own === PHASES.SEC) return own;

      const fromCatalogue = (Array.isArray(phaseCatalog) ? phaseCatalog : [])
        .find((p) => String(p.id) === String(user?.phaseId));
      const code = String(fromCatalogue?.code || '').trim().toLowerCase();
      if (code === PHASES.NUR_PRI || code === PHASES.SEC) return code;
    }

    return null;
  })();

  // Determine which phases are available based on the school's ACTUAL assigned phases
  const availablePhases = useMemo(() => {
    // Fixed-role users always locked to their phase
    if (fixedPhase) return [{ code: fixedPhase }];
    // If we've loaded the school's phases from the backend, use those
    if (Array.isArray(schoolPhases) && schoolPhases.length > 0) {
      return schoolPhases.map(p => ({ code: p.code, id: p.id, name: p.name }));
    }
    // Fallback while loading (or for non-school users like SystemAdmin): both
    if (isSchoolAdmin) return [{ code: PHASES.NUR_PRI }, { code: PHASES.SEC }];
    return [{ code: PHASES.NUR_PRI }, { code: PHASES.SEC }];
  }, [fixedPhase, schoolPhases, isSchoolAdmin]);

  const canSwitchPhases = useCallback(() => {
    // Can only switch if SchoolAdmin AND school has more than 1 phase
    if (!isSchoolAdmin) return false;
    return availablePhases.length > 1;
  }, [isSchoolAdmin, availablePhases]);

  const switchPhase = useCallback((code) => {
    if (!canSwitchPhases()) return;
    updatePhase(code);
  }, [canSwitchPhases, updatePhase]);

  // Ensure activePhase has a sensible default once role / school phases are known
  useEffect(() => {
    if (fixedPhase) {
      if (activePhase !== fixedPhase) setActivePhase(fixedPhase);
      return;
    }
    // If school has exactly one phase, lock to it
    if (Array.isArray(schoolPhases) && schoolPhases.length === 1) {
      const onlyCode = schoolPhases[0].code;
      if (activePhase !== onlyCode) setActivePhase(onlyCode);
      return;
    }
    // If school has multiple phases and we have no active phase yet, default to first
    if (!activePhase && Array.isArray(schoolPhases) && schoolPhases.length > 1) {
      setActivePhase(schoolPhases[0].code);
      return;
    }
    // If activePhase is set but doesn't belong to this school's phases, reset it
    if (activePhase && Array.isArray(schoolPhases) && schoolPhases.length > 0) {
      const valid = schoolPhases.some(p => p.code === activePhase);
      if (!valid) setActivePhase(schoolPhases[0].code);
      return;
    }
    // Fallback for first load before school phases are fetched
    if (!activePhase && isSchoolAdmin) {
      setActivePhase(PHASES.NUR_PRI);
    }
  }, [activePhase, fixedPhase, isSchoolAdmin, schoolPhases]);

  // Fetch the school's actual assigned phases
  useEffect(() => {
    if (!user?.schoolId) return; // Only for school-level users
    // Do not hit authenticated endpoints if we are not logged in yet.
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (!token) return;
    let mounted = true;
    (async () => {
      try {
        const resp = await fetchJson('/api/phases/school');
        const arr = Array.isArray(resp?.phases) ? resp.phases : [];
        if (mounted && arr.length > 0) {
          setSchoolPhases(arr);
          try { localStorage.setItem('schoolPhases', JSON.stringify(arr)); } catch (_) {}
        }
      } catch (_) {
        // If endpoint fails, leave schoolPhases as-is (fallback to both)
      }
    })();
    return () => { mounted = false; };
  }, [user?.schoolId]);

  // Load global phases list for code<->id mapping and cache it
  useEffect(() => {
    // Backend may protect this endpoint; avoid calling it while logged out.
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (!token) return;
    let mounted = true;
    (async () => {
      try {
        const resp = await fetchJson('/api/phases');
        const arr = Array.isArray(resp?.phases) ? resp.phases : [];
        if (mounted) setPhaseCatalog(arr);
        try { localStorage.setItem('phaseCatalog', JSON.stringify(arr)); } catch (_) {}
      } catch (_) {}
    })();
    return () => { mounted = false; };
  }, []);

  const value = useMemo(() => ({
    activePhase,
    setActivePhase: updatePhase,
    getPhaseLabel,
    getPhaseDescription,
    availablePhases,
    canSwitchPhases,
    switchPhase,
    user,
    PHASES,
  }), [activePhase, updatePhase, getPhaseLabel, getPhaseDescription, availablePhases, canSwitchPhases, switchPhase, user]);

  return <PhaseContext.Provider value={value}>{children}</PhaseContext.Provider>;
}

export function usePhase() {
  return useContext(PhaseContext) || { activePhase: '', setActivePhase: () => {}, getPhaseLabel: () => '—' };
}